// Phase 10 — Dashboard widget styles.
//
// Token-only CSS. Premium intelligence dashboard aesthetic.

import type { CSSProperties } from "react";
import { cardLow, cardMedium } from "../../styles/elevation.ts";
import { transitionStandard } from "../../styles/motion.ts";

// ─── Shell ────────────────────────────────────────────────────

export const dashRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
};

export const dashHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};

export const dashTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

export const dashSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  marginTop: "4px",
};

export const dashMeta: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
  textAlign: "right",
};

// ─── Widget grid ──────────────────────────────────────────────

export const widgetGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  gap: "var(--sz-space-3)",
};

export const widgetGridLarge: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
  gap: "var(--sz-space-3)",
};

// ─── Widget card ──────────────────────────────────────────────

export const widgetCard: CSSProperties = {
  ...cardMedium,
  ...transitionStandard,
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  borderTop: "3px solid var(--sz-amber)",
  minHeight: "120px",
};

export const widgetCardAccent: CSSProperties = {
  ...widgetCard,
  borderTop: "3px solid var(--sz-success-text)",
};

export const widgetCardWarning: CSSProperties = {
  ...widgetCard,
  borderTop: "3px solid var(--sz-error-text)",
};

export const widgetCardInfo: CSSProperties = {
  ...widgetCard,
  borderTop: "3px solid var(--sz-info-text)",
};

export const widgetLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const widgetValue: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
  letterSpacing: "1px",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

export const widgetSecondary: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  color: "var(--sz-text-secondary)",
};

export const widgetTrend: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

export const trendUp: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--sz-success-text)",
};

export const trendDown: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--sz-error-text)",
};

export const trendFlat: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--sz-text-muted)",
};

// ─── Progress bar ─────────────────────────────────────────────

export const progressTrack: CSSProperties = {
  height: "6px",
  borderRadius: "3px",
  background: "var(--sz-surface-recessed)",
  overflow: "hidden",
};

export const progressFill: CSSProperties = {
  height: "100%",
  borderRadius: "3px",
  background: "var(--sz-amber)",
  transition: "width 0.4s ease",
};

export const progressFillGreen: CSSProperties = {
  ...progressFill,
  background: "var(--sz-success-text)",
};

export const progressFillRed: CSSProperties = {
  ...progressFill,
  background: "var(--sz-error-text)",
};

// ─── Mini list inside widget ──────────────────────────────────

export const miniList: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-1)",
};

export const miniRow: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "var(--sz-space-2)",
};

export const miniLabel: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-xs)",
  color: "var(--sz-text-secondary)",
};

export const miniValue: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  fontWeight: 600,
  color: "var(--sz-text-primary)",
};

// ─── Checklist ────────────────────────────────────────────────

export const checkItem: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--sz-space-2)",
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "0.5px",
  color: "var(--sz-text-secondary)",
};

export const checkDot: CSSProperties = {
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  flexShrink: 0,
};

export const checkDotDone: CSSProperties = {
  ...checkDot,
  background: "var(--sz-success-text)",
};

export const checkDotPending: CSSProperties = {
  ...checkDot,
  background: "var(--sz-amber)",
};

export const checkDotSkipped: CSSProperties = {
  ...checkDot,
  background: "var(--sz-text-muted)",
};

// ─── Section ──────────────────────────────────────────────────

export const sectionTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.2rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
};

// ─── Empty ────────────────────────────────────────────────────

export const emptyState: CSSProperties = {
  ...cardLow,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-6) var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
};

export const emptyIcon: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "2rem",
  color: "var(--sz-text-muted)",
  opacity: 0.5,
};

export const emptyTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.1rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-secondary)",
};

export const emptyNote: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--sz-text-muted)",
  textAlign: "center",
  maxWidth: "400px",
};

export const phantomBadge: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  opacity: 0.5,
  textAlign: "center",
  padding: "var(--sz-space-2) 0",
};
